import {Controller} from "@hotwired/stimulus"
import {OverlayScrollbars, ClickScrollPlugin} from 'overlayscrollbars';
import {useZoomListener} from "./mixins/use_zoom_listener.js";
import {useResizeListener} from "./mixins/use_resize_listener.js";
import {useLeftPanelChangeListener} from "./mixins/use_left_panel_change_listener.js";
import {colord} from "colord";

/**
 * BidirectionalScrollController is simple controller that adds
 * scrollbar(s) visible on all devices with unified look.
 *
 * Usage:
 *  %div{data: { controller: 'bidirectional-scroll',
 *               bidirectional_scroll_horizontal_thickness_value: 'thin',
 *               bidirectional_scroll_vertical_thickness_value: 'standard',
 *               bidirectional_scroll_direction_value: 'vertical',
 *               bidirectional_scroll_handle_distance_value: '4px',
 *               bidirectional_scroll_scrollbar_background_color_value: '#E2E8F0',
 *               bidirectional_scroll_scrollbar_background_opacity_value: '0.5',
 *               bidirectional_scroll_handle_color_value: '#A0AEC0',
 *               bidirectional_scroll_track_click_behaviour_value: 'instant',
 *               bidirectional_scroll_track_click_animation_duration_value: 300, }}
 *  ... content ...
 *
 *  horizontalThickness, verticalThickness
 *  - 'standard' | 'medium' | 'thin'
 *
 *  direction
 *  - 'vertical' | 'horizontal' | 'both'
 *
 *  scrollbarBackgroundColor
 *  - any valid CSS color value as a string
 *
 *  scrollbarBackgroundOpacity
 *  - number between 0 and 1
 *
 *  handleColor
 *  - any valid CSS color value as a string
 *
 *  trackClickBehaviour
 *  - 'none' | 'step' | 'instant'
 *
 *  trackClickAnimationDuration
 *  - number of milliseconds to animate the scroll
 */

export default class extends Controller {
  static values = {
    horizontalThickness: {type: String, default: 'standard'},
    verticalThickness: {type: String, default: 'standard'},
    direction: {type: String, default: 'vertical'},
    handleDistance: {type: String, default: '0'},
    scrollbarBackgroundColor: {type: String, default: 'transparent'},
    scrollbarBackgroundOpacity: {type: String, default: '0.05'},
    handleColor: {type: String},
    trackClickBehaviour: {type: String, default: 'instant'},
    trackClickAnimationDuration: {type: Number, default: 300},
  }

  osInstance = null;
  verticalBar = null;
  horizontalBar = null;
  scrollbarBackground = null;
  handleBackground = null;
  verticalTrack = null;
  horizontalTrack = null;
  verticalMousedownHandler = null;
  horizontalMousedownHandler = null;

  resizeObserver = null;

  connect() {
    super.connect();
    OverlayScrollbars.plugin(ClickScrollPlugin);
    useZoomListener(this);
    useResizeListener(this);
    useLeftPanelChangeListener(this);

    this._initOS();
  }

  disconnect() {
    super.disconnect();

    if (this.verticalTrack && this.verticalMousedownHandler) {
      this.verticalTrack.removeEventListener('mousedown', this.verticalMousedownHandler, {capture: true});
    }
    if (this.horizontalTrack && this.horizontalMousedownHandler) {
      this.horizontalTrack.removeEventListener('mousedown', this.horizontalMousedownHandler, {capture: true});
    }

    this.verticalTrack = null;
    this.horizontalTrack = null;
    this.verticalMousedownHandler = null;
    this.horizontalMousedownHandler = null;

    if (this.osInstance) {
      this.osInstance.destroy();
      this.osInstance = null;
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    this.element.style.height = '';
  }

  _initOS() {
    const direction = this.directionValue;

    let clickScrollValue;

    switch (this.trackClickBehaviourValue) {
      case 'none':
        clickScrollValue = false;
        break;
      case 'step':
        clickScrollValue = true;
        break;
      case 'instant':
        clickScrollValue = false;
        break;
      default:
        clickScrollValue = false;
    }

    let overflow = {
      x: 'auto',
      y: 'scroll',
    };

    switch (direction) {
      case 'horizontal':
        overflow.x = 'scroll';
        overflow.y = 'auto';
        break;
      case 'vertical':
        overflow.x = 'auto';
        overflow.y = 'scroll';
        break;
      case 'both':
        overflow.x = 'scroll';
        overflow.y = 'scroll';
        break;
    }

    this.osInstance = OverlayScrollbars(
      {target: this.element},
      {
        overflow: overflow,
        showNativeOverlaidScrollbars: false,
        scrollbars: {
          theme: 'enectiva-standard',
          visibility: 'visible',
          autoHide: 'never',
          clickScroll: clickScrollValue,
        },
      },
      {
        initialized: () => this._setAndApply(),
        updated: () => this._setAndApply(),
      }
    );
  }

  _setAndApply() {
    this._setScrollBars();
    this._applyCustomCSS();
  }

  _setScrollBars() {
    if (!this.osInstance) return;

    this._cacheScrollBarElements();

    if (this.trackClickBehaviourValue === 'instant') {
      this._enableInstantTrackClicks();
    }
  }

  /**
   * Caches references to scrollbar bars and tracks from the OverlayScrollbars instance.
   * This is called on every OS initialization/update so we always work with
   * the current DOM structure created by the library.
   */
  _cacheScrollBarElements() {
    const elements = this.osInstance.elements();

    this.verticalBar = elements.scrollbarVertical?.scrollbar;
    this.horizontalBar = elements.scrollbarHorizontal?.scrollbar;

    this.verticalTrack = elements.scrollbarVertical?.track;
    this.horizontalTrack = elements.scrollbarHorizontal?.track;
  }

  /**
   * Prepares vertical and horizontal scrollbar tracks to behave like
   * native scrollbars when the user clicks on the track area:
   *  - forces pointer-events on the track
   *  - creates (once) mousedown handlers for vertical/horizontal axis
   *  - rebinds the handlers with capture so they run before OS internals
   */
  _enableInstantTrackClicks() {
    const elements = this.osInstance.elements();
    const verticalTrack = elements.scrollbarVertical?.track;
    const horizontalTrack = elements.scrollbarHorizontal?.track;

    this._forceTrackPointerEvents(verticalTrack);
    this._forceTrackPointerEvents(horizontalTrack);

    this._ensureTrackHandlersInitialized();

    this._rebindTrackHandler(verticalTrack, this.verticalMousedownHandler);
    this._rebindTrackHandler(horizontalTrack, this.horizontalMousedownHandler);
  }

  _forceTrackPointerEvents(track) {
    if (!track) return;
    track.setAttribute('style', 'pointer-events: auto !important;');
    /* Force reflow so the browser applies the new pointer-events value immediately. */
    track.offsetHeight;
  }

  _ensureTrackHandlersInitialized() {
    if (!this.verticalMousedownHandler) {
      this.verticalMousedownHandler = (e) => this._handleTrackMousedown(e, 'vertical');
    }
    if (!this.horizontalMousedownHandler) {
      this.horizontalMousedownHandler = (e) => this._handleTrackMousedown(e, 'horizontal');
    }
  }

  _rebindTrackHandler(track, handler) {
    if (!track || !handler) return;
    track.removeEventListener('mousedown', handler, {capture: true});
    track.addEventListener('mousedown', handler, {capture: true});
  }

  _handleTrackMousedown(e, axis) {
    if (e.target.closest('.os-handle') || e.target.closest('.os-scrollbar-handle')) return;

    e.stopPropagation();
    e.preventDefault();

    const elements = this.osInstance.elements();
    const track = axis === 'vertical' ? elements.scrollbarVertical?.track : elements.scrollbarHorizontal?.track;
    if (!track || !this.osInstance) return;

    const trackRect = track.getBoundingClientRect();
    const clickPos = axis === 'vertical' ? e.clientY - trackRect.top : e.clientX - trackRect.left;
    const trackSize = axis === 'vertical' ? trackRect.height : trackRect.width;
    const targetRatio = Math.max(0, Math.min(1, clickPos / trackSize));

    const viewport = elements.viewport;
    const contentSize = axis === 'vertical' ? viewport.scrollHeight : viewport.scrollWidth;
    const viewportSize = axis === 'vertical' ? viewport.clientHeight : viewport.clientWidth;
    const maxScroll = Math.max(0, contentSize - viewportSize);
    const targetScroll = targetRatio * maxScroll;

    this._animateScroll(axis, targetScroll, this.trackClickAnimationDurationValue);
  }

  _animateScroll(axis, target, duration) {
    const elements = this.osInstance.elements();
    const viewport = elements.viewport;
    const start = axis === 'vertical' ? viewport.scrollTop : viewport.scrollLeft;
    const startTime = performance.now();

    const easeOutQuad = (t) => t * (2 - t);

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOutQuad(progress);
      const current = start + (target - start) * eased;

      if (axis === 'vertical') {
        viewport.scrollTop = current;
      } else {
        viewport.scrollLeft = current;
      }

      if (progress < 1) requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
  }

  _applyCustomCSS() {
    if (this.verticalBar && this.horizontalBar) {
      this._applyCustomAxisPadding();
      this._applyScrollbarCustomColors();
      this._applyHandleCustomColors();
    }
  }

  _applyCustomAxisPadding() {
    this.verticalBar.style.setProperty('--os-padding-axis', this.handleDistanceValue);
    this.horizontalBar.style.setProperty('--os-padding-axis', this.handleDistanceValue);
  }

  _applyScrollbarCustomColors() {
    this._convertColor();

    this.verticalBar.style.setProperty('--os-track-bg', this.scrollbarBackground);
    this.horizontalBar.style.setProperty('--os-track-bg', this.scrollbarBackground);
    this.verticalBar.style.setProperty('--os-track-bg-hover', this.scrollbarBackground);
    this.horizontalBar.style.setProperty('--os-track-bg-hover', this.scrollbarBackground);
    this.verticalBar.style.setProperty('--os-track-bg-active', this.scrollbarBackground);
    this.horizontalBar.style.setProperty('--os-track-bg-active', this.scrollbarBackground);
  }

  _applyHandleCustomColors() {
    if (this.hasHandleColorValue) {
      this.verticalBar.style.setProperty('--os-handle-bg', this.handleColorValue);
      this.horizontalBar.style.setProperty('--os-handle-bg', this.handleColorValue);
      this.verticalBar.style.setProperty('--os-handle-bg-hover', this.handleBackground);
      this.horizontalBar.style.setProperty('--os-handle-bg-hover', this.handleBackground);
      this.verticalBar.style.setProperty('--os-handle-bg-active', this.handleBackground);
      this.horizontalBar.style.setProperty('--os-handle-bg-active', this.handleBackground);
    }
  }

  _convertColor() {
    this.scrollbarBackground = colord(this.scrollbarBackgroundColorValue).alpha(parseFloat(this.scrollbarBackgroundOpacityValue)).toRgbString();
    if (this.hasHandleColorValue) {
      this.handleBackground = colord(this.handleColorValue).darken(0.2).toRgbString();
    }
  }

  onResizeChange() {
    this._initOS();
  }

  onZoomChange() {
    this._initOS();
  }

  onLeftPanelChange() {
    this._initOS();
  }
}